import { useMemo, useState } from 'react'

import type { PickerRow, UidGroupRow } from 'api/canonicalPicker.types'
import { locationsApi } from 'api/locations'
import PickerRowLabel from 'components/AI/CanonicalPicker/PickerRowLabel'
import { UID_ROW_STYLE } from 'components/AI/CanonicalPicker/uidRowStyles'
import { MultiSelectOption, SelectOption } from 'controlled-components'
import { buildTranslate } from 'locales'
import type { PickerSource } from 'slices/aiConversations/pickerTypes'
import type { SourceSelection } from 'slices/aiSegmentBuilder/picker'
import { generateId } from 'utilities/uniqueId'

import UidGroupOptionsContainer from './UidGroupOptions.container'

const t = buildTranslate('components.ai.picker.canonical.global')

type Props = {
  source: PickerSource
  rows: PickerRow[]
  selection: SourceSelection
  isMultiselect: boolean
  /** UIDs searched for: their rows are marked, and a global holding one as a store UID opens */
  highlightedUids?: ReadonlySet<string>
  onPick: (row: PickerRow) => void
  onPickGroup: (group: UidGroupRow) => void
}

const NO_UIDS: ReadonlySet<string> = new Set()

/** The groups that hold one of the uids as a store UID rather than as the group itself */
export const groupsHoldingStoreUids = (
  rows: PickerRow[],
  uids: ReadonlySet<string>
): string[] =>
  rows.flatMap(row =>
    row.kind === 'uidGroup' &&
    !uids.has(row.uidGroup) &&
    (row.storeUids ?? []).some(uid => uids.has(uid))
      ? [row.id]
      : []
  )

/**
 * The options of an open dropdown. A fixed list's UID groups can open to
 * their store UIDs; which ones are open lasts while the list is the same,
 * starting with the globals that hold a searched UID.
 */
const CanonicalOptionsContainer = ({
  source,
  rows,
  selection,
  isMultiselect,
  highlightedUids = NO_UIDS,
  onPick,
  onPickGroup,
}: Props) => {
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(
    () => new Set(groupsHoldingStoreUids(rows, highlightedUids))
  )
  const [globalDescriptionId] = useState(() => `global-uid-${generateId()}`)
  const hasUids = rows.some(row => row.kind !== 'product')
  const { data, isError: locationsFailed } = locationsApi.useGetLocationsQuery(
    {},
    { skip: !hasUids }
  )
  const locationNames = useMemo(
    () =>
      new Map(
        (data?.locations ?? []).map(location => [location.id, location.name])
      ),
    [data]
  )

  const toggleExpanded = (id: string) => {
    const next = new Set(expandedIds)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setExpandedIds(next)
  }

  const Option = isMultiselect ? MultiSelectOption : SelectOption

  return (
    <>
      <span id={globalDescriptionId} hidden>
        {t('description')}
      </span>
      {rows.map(row =>
        row.kind === 'uidGroup' && isMultiselect ? (
          <UidGroupOptionsContainer
            key={row.id}
            source={source}
            group={row}
            selection={selection}
            locationNames={locationNames}
            locationsFailed={locationsFailed}
            isExpanded={expandedIds.has(row.id)}
            highlightedUids={highlightedUids}
            globalDescriptionId={globalDescriptionId}
            onToggleExpanded={() => toggleExpanded(row.id)}
            onPickGroup={() => onPickGroup(row)}
            onPickStoreUid={onPick}
          />
        ) : (
          <Option
            key={row.id}
            value={row.id}
            selected={row.id in selection.rows}
            {...(row.kind !== 'product' && isMultiselect
              ? { overrides: UID_ROW_STYLE }
              : {})}
            onSelect={() => onPick(row)}
          >
            <PickerRowLabel
              row={row}
              locationNames={locationNames}
              locationsFailed={locationsFailed}
            />
          </Option>
        )
      )}
    </>
  )
}

export default CanonicalOptionsContainer
